// Library Methods
import { useCallback, useMemo } from "react";

// Components
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import Circle from "src/assets/shapes/Circle/Circle";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";
import Dash from "src/assets/shapes/Dash/Dash";
import GraphRings from "src/assets/shapes/GraphRings/GraphRings";
import Square from "src/assets/shapes/Square/Square";
import Triangle from "src/assets/shapes/Triangle/Triangle";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

// Types
import { ChartSpotType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 624;

type RLFastGraphC40Type = {
  vfScreeningSpots: Array<ChartSpotType>;
  gridType: string | undefined;
  eyeType: string;
  isLiveUpdate?: boolean;
  containerSize?: number;
};

const RLFastC40Graph = ({
  vfScreeningSpots,
  gridType,
  eyeType,
  isLiveUpdate = false,
  containerSize = DEFAULT_CONTAINER_SIZE,
}: RLFastGraphC40Type) => {
  const { LEFT } = eyeTypeEnums;

  const size = useMemo(() => containerSize * 0.7, [containerSize]);

  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / DEFAULT_CONTAINER_SIZE, [size]);
  const tickNum = useMemo(() => 3, []);

  const borderWidth = useMemo(() => 2, []);
  const halfHeightPx = `${(size - borderWidth) / 2}px`;

  const calculateXPaddingC40Graph = useCallback(
    (xFloor: number) => {
      // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
      const jumpAxis = 306 * scaleFactor;
      const paddingPerUnit = 10.4 * scaleFactor;

      if (xFloor <= 0) {
        return 310 * scaleFactor - xFloor * -paddingPerUnit;
      } else {
        return xFloor * paddingPerUnit + jumpAxis;
      }
    },
    [scaleFactor]
  );

  const calculateYPaddingC40Graph = useCallback(
    (yFloor: number) => {
      // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
      const jumpAxis = 306 * scaleFactor;

      if (yFloor >= 0) {
        return 308 * scaleFactor - yFloor * 10.4 * scaleFactor;
      } else {
        return jumpAxis + yFloor * -10.6 * scaleFactor;
      }
    },
    [scaleFactor]
  );

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <ChartCross
        height={size}
        width={size}
        tickNum={tickNum}
        tickSize={tickSize}
        gridType={gridType}
      />

      <GraphRings size={size} tickNum={tickNum} ariaLabel="c40-ring" />

      {/* Triangle to show the blind spot */}
      <div
        style={{
          position: "absolute",
          marginTop: `calc(${halfHeightPx} - 31px)`,
          // For left eyes, place the triangle on the left side of the grid. For right eyes, it's the opposite.
          marginLeft:
            eyeType === LEFT
              ? `calc(${(size * 1) / 6}px + ${(size / DEFAULT_CONTAINER_SIZE) * 35}px)`
              : `calc(${(size * 5) / 6}px - ${(size / DEFAULT_CONTAINER_SIZE) * 35}px)`,
        }}
      >
        <Triangle />
      </div>

      {/* Unseen spots */}
      {vfScreeningSpots
        .filter((spot) => !spot.seen && spot.completed)
        .map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingC40Graph(spot.x),
                paddingTop: calculateYPaddingC40Graph(spot.y),
              }}
            >
              <Square size={scaleFactor * 8} />
            </div>
          );
        })}

      {/* Seen spots */}
      {vfScreeningSpots
        .filter((spot) => spot.seen)
        .map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingC40Graph(spot.x),
                paddingTop: calculateYPaddingC40Graph(spot.y),
              }}
            >
              <Circle size={scaleFactor * 10} />
            </div>
          );
        })}

      {/* In progress spots (live update modal) */}
      {/* Display an orange circle by default or a green circle on the "current spot" */}
      {vfScreeningSpots
        .filter((spot) => !spot.completed && spot.decibelValue) // filter uncompleted spots with a valid decibelValue
        .map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingC40Graph(spot.x),
                paddingTop: calculateYPaddingC40Graph(spot.y),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={scaleFactor * 8} />
              ) : (
                <ColoredCircle
                  isOrange={!spot.current}
                  size={scaleFactor * 8}
                />
              )}
            </div>
          );
        })}
    </main>
  );
};

export default RLFastC40Graph;
